import { AVATAR_LIMITS } from "@uzverse/contracts";
import type { AvatarSpec } from "@uzverse/contracts";
import { hashOf } from "./hash.js";
import { ROCKETBOX_IDS, motionLookOf } from "./rocketbox/looks.js";
import { decodeMh, defaultMh, encodeMh, migrateLegacy, normalizeMh } from "./mh/spec.js";
import type { MhSpec } from "./mh/spec.js";

export type { AvatarSpec };
export { hashOf };
export type Body = AvatarSpec["body"];
/** `mh`: a MakeHuman character (the default, `mh` holds it); `office`: a Rocketbox office person; `classic`: the Quaternius body. */
export type Style = NonNullable<AvatarSpec["style"]>;

/**
 * What a character can look like. Two families of bundled models: the `office` people (Microsoft
 * Rocketbox, MIT, `public/world/models/rocketbox/`: 18 men and women in business and smart office
 * wear, the same people who work in the buildings; `look` picks one) and the `classic` Quaternius
 * body (`public/world/models/characters/`), dressed by skin tone, hairstyle and color, and an
 * outfit with a color. Every field is an index into the palettes below, so the saved spec is tiny,
 * can be validated by range, and survives a palette growing later.
 */

/** Skin tones (sRGB), light to deep. The model's texture is a medium tan; the tint scales it. */
export const SKIN_TONES = [
  "#efcfb4",
  "#e3b896",
  "#d2a07a",
  "#bb8662",
  "#a06c48",
  "#845236",
  "#5f3824",
  "#40251a",
] as const;
/** Mean sRGB color of the model's own skin texture (measured), the tint's reference. */
const SKIN_BASE = [168, 117, 81] as const;

export const HAIR_COLORS = [
  "#1b1512",
  "#3a2618",
  "#5b3a22",
  "#7b4a26",
  "#a0522d",
  "#c9a15a",
  "#9a9a9a",
  "#b8472a",
] as const;

export const OUTFIT_COLORS = [
  "#2f4a8a",
  "#e9e6df",
  "#b4343f",
  "#2a8c82",
  "#d9a22a",
  "#6f4fb0",
  "#4c8a45",
  "#24262c",
  "#7d838c",
  "#d9708f",
] as const;

export interface HairStyle {
  id: string;
  /** Nodes of the character file shown for this style. */
  nodes: readonly string[];
  bodies: readonly Body[];
}
/** Hairstyle slots (the spec's `hair` index). */
export const HAIR_STYLES: readonly HairStyle[] = [
  { id: "buzzed", nodes: ["Hair_Buzzed"], bodies: ["male", "female"] },
  { id: "parted", nodes: ["Hair_SimpleParted"], bodies: ["male", "female"] },
  { id: "long", nodes: ["Hair_Long"], bodies: ["male", "female"] },
  { id: "buns", nodes: ["Hair_Buns"], bodies: ["male", "female"] },
  { id: "short-f", nodes: ["Hair_BuzzedFemale"], bodies: ["male", "female"] },
  { id: "beard", nodes: ["Hair_Buzzed", "Hair_Beard"], bodies: ["male"] },
];

export interface Outfit {
  id: string;
  top: "Top_short" | "Top_long" | "Top_jacket";
  bottom: "Bottom_long" | "Bottom_shorts";
  /** Trouser / short color (sRGB). */
  bottomColor: string;
}
export const OUTFITS: readonly Outfit[] = [
  { id: "tee", top: "Top_short", bottom: "Bottom_long", bottomColor: "#6b5f4c" },
  { id: "shirt", top: "Top_long", bottom: "Bottom_long", bottomColor: "#2e3139" },
  { id: "blazer", top: "Top_jacket", bottom: "Bottom_long", bottomColor: "#23252b" },
  { id: "shorts", top: "Top_short", bottom: "Bottom_shorts", bottomColor: "#4c6a8f" },
  { id: "hoodie", top: "Top_jacket", bottom: "Bottom_long", bottomColor: "#3d5675" },
];

export const SPEC_LIMITS = AVATAR_LIMITS;

const clampInt = (n: unknown, max: number): number => {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.trunc(n) : 0;
  return Math.max(0, Math.min(max - 1, v));
};

/** The office looks a body may wear, as spec `look` indices (business wear first). */
export function lookOptions(body: Body): number[] {
  const g = body === "female" ? "f" : "m";
  return ROCKETBOX_IDS.flatMap((id, i) => (motionLookOf(id) === g ? [i] : []));
}

/** Office looks too casual for a default (a pink T-shirt and denim shorts); still selectable. */
const NOT_DEFAULT = new Set(["office-f4"]);

/** The business-casual looks (smart office wear) of a body: where a default person comes from. */
export function casualLooks(body: Body): number[] {
  return lookOptions(body).filter((i) => {
    const id = ROCKETBOX_IDS[i]!;
    return id.startsWith("office-") && !NOT_DEFAULT.has(id);
  });
}

/** The Rocketbox folder id of a spec's office look. */
export function rocketboxIdOf(spec: AvatarSpec): string {
  return ROCKETBOX_IDS[normalizeSpec(spec).look ?? 0]!;
}

/**
 * Keeps every index inside its palette, the hairstyle valid for the body and the office look one
 * of the body's. A spec saved before styles existed (no `style`) is an office person: the look is
 * then picked from its other fields, so it stays the same person on every device.
 */
export function normalizeSpec(spec: Partial<AvatarSpec>): AvatarSpec {
  const body: Body = spec.body === "female" ? "female" : "male";
  let hair = clampInt(spec.hair, HAIR_STYLES.length);
  if (!HAIR_STYLES[hair]!.bodies.includes(body)) hair = 0;
  const skin = clampInt(spec.skin, SKIN_TONES.length);
  const outfit = clampInt(spec.outfit, OUTFITS.length);
  const outfitColor = clampInt(spec.outfitColor, OUTFIT_COLORS.length);
  const own = lookOptions(body);
  let look = typeof spec.look === "number" ? clampInt(spec.look, ROCKETBOX_IDS.length) : -1;
  if (!own.includes(look)) {
    const pool = casualLooks(body);
    look = pool[(skin + hair * 3 + outfit * 5 + outfitColor * 7) % pool.length]!;
  }
  const mh = spec.style === "mh" && spec.mh ? normalizeMh(spec.mh) : null;
  return {
    body: mh ? mh.sex : body,
    style: mh ? "mh" : spec.style === "classic" ? "classic" : "office",
    ...(mh ? { mh } : {}),
    look,
    skin,
    hair,
    hairColor: clampInt(spec.hairColor, HAIR_COLORS.length),
    outfit,
    outfitColor,
  };
}

/**
 * The MakeHuman character of any spec: its own `mh`, or a palette spec (classic or office era)
 * migrated to the nearest MakeHuman preset.
 */
export function mhOf(spec: Partial<AvatarSpec>): MhSpec {
  if (spec.style === "mh" && spec.mh) return normalizeMh(spec.mh);
  const s = normalizeSpec({ ...spec, style: spec.style === "classic" ? "classic" : "office" });
  return migrateLegacy(s);
}

/** A full settings spec around a MakeHuman character (the palette fields are older clients' fallback). */
export function withMh(mh: MhSpec): AvatarSpec {
  const m = normalizeMh(mh);
  return normalizeSpec({
    body: m.sex,
    style: "mh",
    mh: m,
    skin: Math.min(SKIN_TONES.length - 1, m.skin),
    hair: 0,
    hairColor: 1,
    outfit: 1,
    outfitColor: 0,
  });
}

/** The hairstyles a body may wear, as spec indices. */
export function hairOptions(body: Body): number[] {
  return HAIR_STYLES.flatMap((h, i) => (h.bodies.includes(body) ? [i] : []));
}

/**
 * A deterministic, varied classic character for anyone without a saved one (the Quaternius
 * agents, the assistant). Its `look` is set too, so `{ ...specFor(seed), style: "office" }` is a
 * person of the same body.
 */
export function specFor(seed: string): AvatarSpec {
  const h = hashOf(seed);
  const pick = (shift: number, n: number) => ((h >>> shift) ^ (h >>> (shift + 11))) % n;
  const body: Body = pick(0, 2) === 0 ? "male" : "female";
  const opts = hairOptions(body);
  const casual = casualLooks(body);
  return normalizeSpec({
    body,
    style: "classic",
    look: casual[hashOf(`look:${seed}`) % casual.length]!,
    skin: pick(3, SKIN_TONES.length),
    hair: opts[pick(7, opts.length)]!,
    hairColor: pick(13, HAIR_COLORS.length),
    outfit: pick(17, OUTFITS.length),
    outfitColor: pick(23, OUTFIT_COLORS.length),
  });
}

/**
 * The user's character until they choose one: a MakeHuman preset picked by a hash of the name (the
 * user id when there is none) with a seeded face variation, so it is the same person on every
 * device. Always a realistic average build.
 */
export function defaultUserSpec(seed: string, name = ""): AvatarSpec {
  return withMh(defaultMh(seed, name));
}

// ---------------------------------------------------------------- the assistant's `appearance`

const APPEARANCE_PREFIX = "a1:";

/**
 * `appearance` string for the assistant setting (≤ 60 chars): `m1:...` for a MakeHuman character
 * (`encodeMh`), `a1:` + one digit per field for a palette one.
 */
export function encodeAppearance(spec: AvatarSpec): string {
  const s = normalizeSpec(spec);
  if (s.style === "mh" && s.mh) return encodeMh(s.mh);
  return `${APPEARANCE_PREFIX}${s.body === "female" ? "f" : "m"}${s.skin}${s.hair}${s.hairColor}${s.outfit}${s.outfitColor}`;
}

/**
 * Reads an `appearance` string as a MakeHuman character: `m1:` as is, an `a1:` palette character
 * migrated to its nearest preset, "default" and anything unknown a default character of `seed`
 * (the assistant's name).
 */
export function decodeAppearance(appearance: string | undefined, seed: string): AvatarSpec {
  const mh = decodeMh(appearance);
  if (mh) return withMh(mh);
  const legacy = decodeLegacyAppearance(appearance);
  return legacy ? withMh(mhOf(legacy)) : defaultUserSpec(seed, seed);
}

/** The palette (`a1:`) form, or null. */
export function decodeLegacyAppearance(appearance: string | undefined): AvatarSpec | null {
  const m = /^a1:([mf])(\d)(\d)(\d)(\d)(\d)$/.exec(appearance ?? "");
  if (!m) return null;
  return normalizeSpec({
    body: m[1] === "f" ? "female" : "male",
    style: "classic",
    skin: Number(m[2]),
    hair: Number(m[3]),
    hairColor: Number(m[4]),
    outfit: Number(m[5]),
    outfitColor: Number(m[6]),
  });
}

// ---------------------------------------------------------------- colors for the renderer

const lin = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Linear multiplier for the skin material's color: lands the model's tan on the chosen tone. */
export function skinTint(index: number): [number, number, number] {
  const t = hexToRgb(SKIN_TONES[clampInt(index, SKIN_TONES.length)]!);
  return [
    lin(t[0]) / lin(SKIN_BASE[0]),
    lin(t[1]) / lin(SKIN_BASE[1]),
    lin(t[2]) / lin(SKIN_BASE[2]),
  ];
}

/** Hair texture is a pale grey (mean about 0.7), so the tint is lifted to land on the color. */
export const HAIR_GAIN = 1.45;

// ---------------------------------------------------------------- agents in the floors

/** Outfit colors that read as a department: stable hex per department name, or the brand's own. */
const DEPARTMENT_COLORS = [
  "#2f4a8a",
  "#2a8c82",
  "#b4343f",
  "#d9a22a",
  "#6f4fb0",
  "#4c8a45",
  "#3d6fa0",
];

export interface AgentLook {
  spec: AvatarSpec;
  /** Overrides the top's color (a company brand color or a department color), sRGB hex. */
  topColor: string;
}

/** The look of an agent: a deterministic character per id, the top tinted by brand or department. */
export function agentLook(
  agentId: string,
  opts: { brand?: string | undefined; department?: string | undefined } = {},
): AgentLook {
  const spec = specFor(agentId);
  const color =
    opts.brand && /^#[0-9a-f]{6}$/i.test(opts.brand)
      ? opts.brand
      : opts.department
        ? DEPARTMENT_COLORS[hashOf(opts.department) % DEPARTMENT_COLORS.length]!
        : OUTFIT_COLORS[spec.outfitColor]!;
  return { spec, topColor: color };
}
